import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { copilotApi, ChatMessage, HealthCheck, Source } from '../api/copilot';
import SourceManagement from '../components/copilot/SourceManagement';
import { SparklesIcon, PaperAirplaneIcon, TrashIcon, ExclamationTriangleIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function BirdChatter() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(`session_${Date.now()}`);
  const [sources, setSources] = useState<Source[]>([]);
  const [useKnowledge, setUseKnowledge] = useState(true);
  const [useCodebase, setUseCodebase] = useState(true);
  const [useSources, setUseSources] = useState(true);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [healthStatus, setHealthStatus] = useState<HealthCheck | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkHealth();
    loadSources();
    loadHistory();
  }, []);

  const checkHealth = async () => {
    try {
      const health = await copilotApi.checkHealth();
      setHealthStatus(health);
      if (!health.aiGateway.configured) {
        toast.error(
          'AI gateway is not configured. Set AI_GATEWAY_API_KEY or OPENAI_API_KEY on the API.',
        );
      }
    } catch (error: any) {
      console.error('Failed to check health:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSources = async () => {
    try {
      const response = await copilotApi.getSources();
      setSources(response.sources);
    } catch (error: any) {
      console.error('Failed to load sources:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await copilotApi.getHistory(sessionId);
      setMessages(response.messages);
    } catch (error: any) {
      console.error('Failed to load history:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await copilotApi.chat(input.trim(), {
        sessionId,
        useKnowledge,
        useCodebase,
        useSources,
        useWebSearch,
      });

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.message,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Reload sources in case new ones were added
      if (response.sources) {
        loadSources();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to get response');
      console.error('Chat error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await copilotApi.clearHistory(sessionId);
      setMessages([]);
      toast.success('History cleared');
    } catch (error: any) {
      toast.error('Failed to clear history');
    }
  };

  const handleSourceUpdate = () => {
    loadSources();
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <div className="w-80 border-r border-gray-200 bg-white p-4 overflow-y-auto">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">BirdChatter Settings</h2>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useKnowledge}
                onChange={(e) => setUseKnowledge(e.target.checked)}
                className="rounded border-gray-300 text-cyan-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Use Knowledge Library</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useCodebase}
                onChange={(e) => setUseCodebase(e.target.checked)}
                className="rounded border-gray-300 text-cyan-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Use Codebase Context</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useSources}
                onChange={(e) => setUseSources(e.target.checked)}
                className="rounded border-gray-300 text-cyan-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Use Loaded Sources</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useWebSearch}
                onChange={(e) => setUseWebSearch(e.target.checked)}
                className="rounded border-gray-300 text-cyan-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Enhanced Web Search</span>
            </label>
          </div>

          <Link
            to="/knowledge"
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-cyan-600 hover:text-cyan-700"
          >
            <BookOpenIcon className="h-4 w-4" />
            Manage Knowledge Library
          </Link>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-slate-900">Sources</h2>
            <button
              onClick={handleClearHistory}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <TrashIcon className="h-4 w-4" />
              Clear
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-2">
            {sources.length} source{sources.length !== 1 ? 's' : ''} loaded
          </p>
        </div>

        <SourceManagement
          sources={sources}
          onSourceUpdate={handleSourceUpdate}
        />
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200/70 bg-white/85 px-6 py-4 backdrop-blur-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SparklesIcon className="h-6 w-6 text-cyan-600" />
              <div>
                <h1 className="gradient-title text-xl font-semibold">BirdChatter</h1>
                <p className="text-sm text-slate-500">
                  Aviculture expert grounded in your Knowledge Library &amp; BirdMan data
                </p>
              </div>
            </div>
            {healthStatus && !healthStatus.aiGateway.configured && (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  AI gateway not configured
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/80">
          {messages.length === 0 && (
            <div className="text-center text-slate-500 mt-20">
              <SparklesIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Welcome to BirdChatter</p>
              <p className="text-sm">
                Ask about aviculture — nutrition, breeding, health, husbandry — or the BirdMan app itself.
              </p>
              <p className="text-xs mt-4 text-slate-400">
                Add books &amp; manuals to your Knowledge Library so answers are grounded in trusted references.
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-3xl rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-slate-900 border border-gray-200'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap">{message.content}</div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
                  <span className="text-sm text-slate-500">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 bg-white p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask BirdChatter anything..."
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

